/**
 * L10 API surface — single import point for the meeting runner.
 *
 * Routes through the real backend client by default. A dev-only escape hatch
 * swaps in the in-memory mocks (``./mocks.ts``) when ``VITE_L10_MOCKS=1`` is
 * set on a ``vite`` dev run, so the whole runner is clickable end-to-end with
 * no backend / DB (used for local QA + screenshots). Production builds fold
 * ``import.meta.env.DEV`` to ``false`` so the mock is never selected at
 * runtime, and ``vite.config.ts`` aliases the mock module to an empty stub for
 * production builds so its seed data is excluded from the bundle entirely.
 */

import * as realClient from "./client";
// Imported via the `@/api/l10/mocks` alias (not `./mocks`) so the production
// build can swap it for an empty stub — see the `resolve.alias` block in
// vite.config.ts. The mock layer is dev-only (gated below on
// `import.meta.env.DEV`), so prod ships the stub and the seed data is dropped.
import * as mockClient from "@/api/l10/mocks";

export * from "./types";

const useMocks =
  import.meta.env.DEV && import.meta.env.VITE_L10_MOCKS === "1";

if (useMocks) {
  // Loud, dev-only breadcrumb so a mock-mode run is never mistaken for the
  // real backend during QA.
  console.warn("[l10] VITE_L10_MOCKS=1 — using in-memory mock data layer");
}

// The real client is the type source of truth — the app is built against its
// signatures. The mock satisfies the same surface structurally at runtime; the
// cast keeps every downstream binding typed as the real client (no union
// widening of return types).
const impl = (useMocks ? mockClient : realClient) as unknown as typeof realClient;

export const {
  LEADERSHIP_TEAM_ID,
  DEFAULT_SECTION_BUDGETS_SECONDS,
  getTeam,
  listMyTeams,
  getAttendees,
  addTeamMember,
  removeTeamMember,
  setMyHomeTeam,
  leaveMyTeam,
  getSession,
  getSessionRecap,
  listTeamRecaps,
  listTeamSeries,
  addProjectMeeting,
  addSalesHuddleMeeting,
  getTeamSalesHuddle,
  listMySalesHuddles,
  updateSeriesAgenda,
  getActiveSession,
  startMeeting,
  setCurrentSection,
  endSection,
  markPresent,
  setRating,
  setRatingNote,
  setIdsFocus,
  setPropsCursor,
  endSession,
  abandonSession,
  pauseSession,
  resumeSession,
  snapshotTodoDenominator,
  setFacilitator,
  listIssues,
  createIssue,
  updateIssueStatus,
  updateIssue,
  solveIssue,
  deleteIssue,
  bulkArchiveIssues,
  listArchivedIssues,
  restoreIssue,
  moveIssue,
  copyIssueTo,
  postScribeChunk,
  pullMeetTranscript,
  getTranscriptPullStatus,
  getMeetStatus,
  listHeadlines,
  createHeadline,
  updateHeadline,
  archiveHeadline,
  restoreHeadline,
  markHeadlineRead,
  clearHeadlineRead,
  deleteHeadline,
  moveHeadline,
  copyHeadlineTo,
  listTodos,
  createTodo,
  toggleTodo,
  setTodoDueDate,
  updateTodo,
  reassignTodo,
  moveTodo,
  copyTodoTo,
  assignTodo,
  linkTodoToIssue,
  snoozeTodo,
  archiveTodo,
  restoreTodo,
  deleteTodo,
  unlinkTodoRock,
  listCascades,
  createCascade,
  listCascadeTargets,
  getScorecardForTeam,
  getRocksForTeam,
  createItemLink,
  listItemLinks,
  deleteItemLink,
} = impl;
